// src/pages/sow-draft/sowDraftHelpers.js
//
// Pure helpers for Draft SOW -- formatting, contract-type detection,
// working-day/hours math, and the JOSS response mapper. Split out of
// SowDraftPage.jsx; none of this depends on state or the API.

export function normalizeList(res) {
  const r = res?.data ?? res;
  if (Array.isArray(r)) return r;
  if (Array.isArray(r?.items)) return r.items;
  return [];
}

export function formatCurrency(amt) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(
    amt || 0
  );
}

export function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

export const CONTRACT_TYPES = { FIXED_BID: "Fixed Bid", TM: "T&M", MANAGED: "Managed Services" };

export function detectContractType(type) {
  const t = (type || "").toLowerCase();
  if (t.includes("fixed") || t.includes("bid")) return CONTRACT_TYPES.FIXED_BID;
  if (t.includes("t&m") || t.includes("time") || t.includes("material")) return CONTRACT_TYPES.TM;
  if (t.includes("managed") || t.includes("service")) return CONTRACT_TYPES.MANAGED;
  return CONTRACT_TYPES.TM;
}

export function calculateWorkingDays(startDate, endDate) {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return 0;
  let count = 0;
  let current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

export function calculateHoursFromDates(startDate, endDate) {
  return calculateWorkingDays(startDate, endDate) * 8;
}

export function formatJossDate(dateStr) {
  if (!dateStr) return "";
  try {
    const s = String(dateStr).trim();
    // Handle DD-MM-YY format (e.g. "22-06-25")
    const ddmmyy = s.match(/^(\d{2})-(\d{2})-(\d{2})$/);
    if (ddmmyy) {
      const [, dd, mm, yy] = ddmmyy;
      const year = parseInt(yy) < 50 ? `20${yy}` : `19${yy}`;
      return `${year}-${mm}-${dd}`;
    }
    // Handle ISO or other standard formats
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toISOString().split("T")[0];
  } catch {
    return String(dateStr);
  }
}

export function mapJossToResources(data) {
  const items = data?.items || data?.resources || data?.data || (Array.isArray(data) ? data : []);
  return items.map((item) => {
    const startDate = formatJossDate(item.START_DATE || item.start_date || item.FROM_DATE || item.from_date || "");
    const endDate = formatJossDate(item.END_DATE || item.end_date || item.TO_DATE || item.to_date || "");
    let hours =
      parseFloat(
        item.QUANTITY || item.quantity || item.ESTIMATED_HOURS || item.HOURS || item.hours ||
          item.TOTAL_HOURS || item.total_hours || 0
      ) || 0;
    if (!hours && startDate && endDate) {
      hours = calculateHoursFromDates(startDate, endDate);
    }
    return {
      role: item.ROLE || item.RESOURCE_ROLE || item.role || item.RESOURCE_NAME || item.resource_name ||
        item.JOB_TITLE || item.job_title || "",
      level: item.PROFILE_CODE || item.profile_code || item.DESIGNATION || item.LEVEL || item.level ||
        item.designation || item.GRADE || item.grade || "",
      location: item.PREMIUM_LOCATION_FLG || item.premium_location_flg || item.LOCATION || item.location ||
        item.RESOURCE_LOCATION || item.resource_location || "",
      count: parseInt(item.COUNT || item.count || item.RESOURCE_COUNT || item.resource_count || item.QTY || item.qty || 1) || 1,
      hours,
      rate: parseFloat(
        item.ADJ_BILL_RATE || item.adj_bill_rate || item.BILL_RATE || item.RATE || item.rate ||
          item.HOURLY_RATE || item.hourly_rate || item.BILLING_RATE || 0
      ) || 0,
      startDate,
      endDate,
      technology: item.TECHNOLOGY || item.technology || "",
      domain: item.DOMAIN || item.domain || "",
      sellingAmount: parseFloat(item.SELLING_AMOUNT || item.selling_amount || 0) || 0,
      status: item.STATUS || item.status || "",
    };
  });
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_ICON_BY_EXT = {
  pdf: "PDF", docx: "DOC", doc: "DOC", pptx: "PPT", ppt: "PPT", xlsx: "XLS", xls: "XLS",
  csv: "CSV", txt: "TXT", png: "IMG", jpg: "IMG", jpeg: "IMG", gif: "IMG", bmp: "IMG", tiff: "IMG",
};

export function getFileIcon(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  return FILE_ICON_BY_EXT[ext] || "FILE";
}
